import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
const COLORS = {
  primary: '#6366f1',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  dark: '#1e293b',
  fab: '#8b5cf6',
  install: '#0ea5e9',
  scenario: '#f97316',
};

const getProgressColor = (val) =>
  val >= 100 ? COLORS.success : val >= 75 ? '#22c55e' : val >= 50 ? COLORS.warning : val > 0 ? '#fb923c' : '#cbd5e1';

const fmtNum = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

const ComparisonBar = ({ current, projected, height = 18 }) => {
  const cappedCurrent = Math.min(Math.max(current, 0), 100);
  const cappedProjected = Math.min(Math.max(projected, 0), 100);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        width: '100%', backgroundColor: '#f1f5f9', borderRadius: height / 2,
        height, overflow: 'hidden', position: 'relative',
      }}>
        <div style={{
          width: `${cappedCurrent}%`, height: '100%',
          backgroundColor: getProgressColor(current),
          borderRadius: height / 2, transition: 'width 0.6s ease-out',
          position: 'absolute', top: 0, left: 0, zIndex: 2,
        }} />
        {projected !== current && (
          <div style={{
            width: `${cappedProjected}%`, height: '100%',
            backgroundColor: `${getProgressColor(projected)}40`,
            borderRadius: height / 2, transition: 'width 0.6s ease-out',
            position: 'absolute', top: 0, left: 0, zIndex: 1,
            border: `1px dashed ${getProgressColor(projected)}`,
          }} />
        )}
        <span style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          fontSize: '9px', fontWeight: '700', color: cappedCurrent > 45 ? '#fff' : '#475569',
          textShadow: cappedCurrent > 45 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none', zIndex: 3,
          whiteSpace: 'nowrap',
        }}>
          {Math.round(current * 10) / 10}%
          {projected !== current && ` \u2192 ${Math.round(projected * 10) / 10}%`}
        </span>
      </div>
    </div>
  );
};

const STORAGE_KEY = 'piping-sov-all-scenarios';

const loadScenario = (projectId) => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}-${projectId}`);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const saveScenario = (projectId, scenario) => {
  try {
    localStorage.setItem(`${STORAGE_KEY}-${projectId}`, JSON.stringify(scenario));
  } catch { /* localStorage full or unavailable */ }
};

const inputStyle = {
  width: 90, padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6,
  fontSize: '13px', textAlign: 'right', outline: 'none', backgroundColor: '#fffbeb',
  transition: 'border-color 0.2s',
};

const PipingSOVAll = ({ projectId, asOf = null }) => {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('sov_code');
  const [sortDir, setSortDir] = useState('asc');
  const [facFt, setFacFt] = useState('');
  const [inputMode, setInputMode] = useState('credit');
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    if (projectId) fetchData();
  }, [projectId, asOf]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!projectId || !data) return;
    const saved = loadScenario(projectId);
    if (saved) {
      setFacFt(saved.facFt || '');
      setInputMode(saved.inputMode || 'credit');
      setOverrides(saved.overrides || {});
    }
  }, [projectId, data]);

  useEffect(() => {
    if (!projectId || !data) return;
    saveScenario(projectId, { facFt, inputMode, overrides });
  }, [projectId, data, facFt, inputMode, overrides]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/piping-sov-analytics/project/${projectId}${asOf ? `?asOf=${asOf}` : ''}`
      );
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching piping SOV analytics:', err);
      setError('Failed to load piping SOV analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = useCallback((field) => {
    setSortDir(prev => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortField(field);
  }, [sortField]);

  const handleOverrideChange = useCallback((sovCode, value) => {
    setOverrides(prev => ({ ...prev, [sovCode]: value }));
  }, []);

  const getProjectedCredit = useCallback((row) => {
    const raw = overrides[row.sov_code];
    if (!raw || raw === '') return row.credit;
    const num = parseFloat(raw) || 0;
    if (inputMode === 'percent') {
      return row.credit * (1 + num / 100);
    }
    return row.credit + num;
  }, [overrides, inputMode]);

  const projectedRows = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows.map(r => {
      const projCredit = getProjectedCredit(r);
      const pctFab = projCredit > 0 ? Math.round((r.earned_credit_fab / projCredit) * 1000) / 10 : 0;
      const pctInstall = projCredit > 0 ? Math.round((r.earned_credit_install / projCredit) * 1000) / 10 : 0;
      const pctTotal = projCredit > 0
        ? Math.round(((r.earned_credit_fab + r.earned_credit_install) / (projCredit * 2)) * 1000) / 10
        : 0;
      return {
        ...r,
        projected_credit: projCredit,
        proj_pct_fab: pctFab,
        proj_pct_install: pctInstall,
        proj_pct_total: pctTotal,
        has_override: !!overrides[r.sov_code] && overrides[r.sov_code] !== '',
      };
    });
  }, [data, getProjectedCredit, overrides]);

  const sortedRows = useMemo(() => {
    return [...projectedRows].sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (typeof av === 'string') {
        if (av === 'Unassigned') return 1;
        if (bv === 'Unassigned') return -1;
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? (av - bv) : (bv - av);
    });
  }, [projectedRows, sortField, sortDir]);

  const projectedTotals = useMemo(() => {
    const totalCredit = projectedRows.reduce((s, r) => s + r.projected_credit, 0);
    const totalFab = projectedRows.reduce((s, r) => s + r.earned_credit_fab, 0);
    const totalInstall = projectedRows.reduce((s, r) => s + r.earned_credit_install, 0);
    return {
      credit: totalCredit,
      earned_credit_fab: totalFab,
      earned_credit_install: totalInstall,
      pct_fab: totalCredit > 0 ? Math.round((totalFab / totalCredit) * 1000) / 10 : 0,
      pct_install: totalCredit > 0 ? Math.round((totalInstall / totalCredit) * 1000) / 10 : 0,
      pct_total: totalCredit > 0
        ? Math.round(((totalFab + totalInstall) / (totalCredit * 2)) * 1000) / 10
        : 0,
    };
  }, [projectedRows]);

  const hasAnyOverride = Object.values(overrides).some(v => v !== '' && v != null);

  const handleClearAll = () => {
    setOverrides({});
    setFacFt('');
  };

  const thStyle = {
    padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0',
    fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase',
    whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
  };
  const tdS = {
    padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '13px',
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 4 }}>{'\u2195'}</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: '3px solid #e2e8f0', borderTop: `3px solid ${COLORS.primary}`,
            borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ color: '#64748b', fontSize: '14px' }}>Loading SOV analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>!</div>
        <div style={{ color: COLORS.danger, fontWeight: '600' }}>{error}</div>
        <button onClick={fetchData} style={{
          marginTop: 12, padding: '8px 20px', backgroundColor: COLORS.primary, color: '#fff',
          border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: '600',
        }}>Retry</button>
      </div>
    );
  }

  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
        No SOV data available for this project.
      </div>
    );
  }

  const { totals } = data;

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: COLORS.dark }}>
            SOV — What-If Scenario
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            Estimate scope growth impact on SOV progress
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasAnyOverride && (
            <button onClick={handleClearAll} style={{
              padding: '8px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 8, cursor: 'pointer', fontSize: '13px', color: COLORS.danger, fontWeight: '500',
            }}>Clear All</button>
          )}
          <button onClick={fetchData} style={{
            padding: '8px 16px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0',
            borderRadius: 8, cursor: 'pointer', fontSize: '13px', color: '#475569', fontWeight: '500',
          }}>Refresh</button>
        </div>
      </div>

      {/* Footage + Mode controls */}
      <div style={{
        backgroundColor: '#fff', borderRadius: 12, padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.25rem',
        border: '1px solid #fef3c7',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-end' }}>
          {/* Total Ft */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
              Total Ft (Current)
            </label>
            <div style={{
              padding: '8px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 6, fontSize: '15px', fontWeight: '700', color: COLORS.dark, minWidth: 120,
            }}>
              {fmtNum(totals.total_ft)}
            </div>
          </div>

          {/* FAC Ft */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: COLORS.scenario, textTransform: 'uppercase', marginBottom: 4 }}>
              FAC Ft (Forecast)
            </label>
            <input
              type="number"
              value={facFt}
              onChange={e => setFacFt(e.target.value)}
              placeholder={fmtNum(totals.total_ft)}
              style={{ ...inputStyle, width: 140, fontSize: '15px', fontWeight: '600', borderColor: '#fed7aa' }}
            />
          </div>

          {facFt && parseFloat(facFt) > 0 && (
            <div style={{
              padding: '8px 14px', backgroundColor: '#fff7ed', borderRadius: 6,
              border: '1px solid #fed7aa', fontSize: '13px',
            }}>
              <span style={{ color: '#92400e', fontWeight: '600' }}>
                {parseFloat(facFt) > totals.total_ft ? '+' : ''}{fmtNum(parseFloat(facFt) - totals.total_ft)} ft
              </span>
              <span style={{ color: '#b45309', marginLeft: 8 }}>
                ({totals.total_ft > 0 ? Math.round(((parseFloat(facFt) - totals.total_ft) / totals.total_ft) * 100) : 0}% growth)
              </span>
            </div>
          )}

          {/* Separator */}
          <div style={{ borderLeft: '1px solid #e5e7eb', height: 40, margin: '0 0.5rem' }} />

          {/* Input mode toggle */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
              Increase Mode
            </label>
            <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setInputMode('credit')}
                style={{
                  padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                  backgroundColor: inputMode === 'credit' ? COLORS.primary : '#f8fafc',
                  color: inputMode === 'credit' ? '#fff' : '#64748b',
                }}
              >+ Credit</button>
              <button
                onClick={() => setInputMode('percent')}
                style={{
                  padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                  backgroundColor: inputMode === 'percent' ? COLORS.primary : '#f8fafc',
                  color: inputMode === 'percent' ? '#fff' : '#64748b',
                  borderLeft: '1px solid #e2e8f0',
                }}
              >% Growth</button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.info}` }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>Current Credit</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.dark }}>{fmtNum(totals.credit)}</div>
          {hasAnyOverride && (
            <div style={{ fontSize: '0.75rem', color: COLORS.scenario, fontWeight: '600', marginTop: 2 }}>
              Projected: {fmtNum(projectedTotals.credit)}
            </div>
          )}
        </div>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.fab}` }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>% Fab</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.dark }}>{totals.pct_fab}%</div>
          {hasAnyOverride && (
            <div style={{ fontSize: '0.75rem', color: COLORS.scenario, fontWeight: '600', marginTop: 2 }}>
              Projected: {projectedTotals.pct_fab}%
            </div>
          )}
        </div>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.install}` }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>% Install</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.dark }}>{totals.pct_install}%</div>
          {hasAnyOverride && (
            <div style={{ fontSize: '0.75rem', color: COLORS.scenario, fontWeight: '600', marginTop: 2 }}>
              Projected: {projectedTotals.pct_install}%
            </div>
          )}
        </div>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.success}` }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>Overall Progress</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.dark }}>{totals.pct_total}%</div>
          {hasAnyOverride && (
            <div style={{ fontSize: '0.75rem', color: COLORS.scenario, fontWeight: '600', marginTop: 2 }}>
              Projected: {projectedTotals.pct_total}%
            </div>
          )}
          <div style={{ marginTop: 6 }}>
            <ComparisonBar
              current={totals.pct_total}
              projected={hasAnyOverride ? projectedTotals.pct_total : totals.pct_total}
              height={10}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => handleSort('sov_code')}>
                  SOV Code <SortIcon field="sov_code" />
                </th>
                <th style={{ ...thStyle, textAlign: 'left', minWidth: 120 }} onClick={() => handleSort('sov_description')}>
                  Description <SortIcon field="sov_description" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#fefce8' }} onClick={() => handleSort('credit')}>
                  Credit <SortIcon field="credit" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#fff7ed', minWidth: 110 }}>
                  Est. Increase
                </th>
                <th style={{ ...thStyle, backgroundColor: '#fff7ed' }} onClick={() => handleSort('projected_credit')}>
                  Projected Credit <SortIcon field="projected_credit" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#f5f3ff' }} onClick={() => handleSort('earned_credit_fab')}>
                  Earned Fab <SortIcon field="earned_credit_fab" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#f5f3ff' }} onClick={() => handleSort('proj_pct_fab')}>
                  % Fab <SortIcon field="proj_pct_fab" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#eff6ff' }} onClick={() => handleSort('earned_credit_install')}>
                  Earned Install <SortIcon field="earned_credit_install" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#eff6ff' }} onClick={() => handleSort('proj_pct_install')}>
                  % Install <SortIcon field="proj_pct_install" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#f0fdf4' }} onClick={() => handleSort('proj_pct_total')}>
                  % Total <SortIcon field="proj_pct_total" />
                </th>
                <th style={{ ...thStyle, width: 200 }}>Progress</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r, idx) => (
                <tr key={r.sov_code} style={{ backgroundColor: r.has_override ? '#fffbeb' : (idx % 2 === 0 ? '#fff' : '#f8fafc') }}>
                  <td style={{ ...tdS, textAlign: 'left', fontWeight: '600', color: r.sov_code === 'Unassigned' ? '#94a3b8' : COLORS.dark }}>
                    {r.sov_code}
                  </td>
                  <td style={{ ...tdS, textAlign: 'left', color: '#64748b', fontSize: '12px' }}>
                    {r.sov_description || '\u2014'}
                  </td>
                  <td style={{ ...tdS, fontWeight: '600' }}>
                    {fmtNum(r.credit)}
                  </td>
                  <td style={{ ...tdS, padding: '6px 8px' }}>
                    <input
                      type="number"
                      value={overrides[r.sov_code] || ''}
                      onChange={e => handleOverrideChange(r.sov_code, e.target.value)}
                      placeholder={inputMode === 'credit' ? '0' : '0%'}
                      style={{
                        ...inputStyle,
                        width: '100%',
                        borderColor: r.has_override ? '#f97316' : '#e2e8f0',
                        backgroundColor: r.has_override ? '#fff7ed' : '#fffbeb',
                      }}
                    />
                  </td>
                  <td style={{ ...tdS, fontWeight: '700', color: r.has_override ? COLORS.scenario : COLORS.dark }}>
                    {fmtNum(r.projected_credit)}
                  </td>
                  <td style={{ ...tdS, backgroundColor: idx % 2 === 0 ? '#faf8ff' : '#f5f3ff' }}>
                    {fmtNum(r.earned_credit_fab)}
                  </td>
                  <td style={{ ...tdS, backgroundColor: idx % 2 === 0 ? '#faf8ff' : '#f5f3ff', fontWeight: '600' }}>
                    <span style={{ color: getProgressColor(r.proj_pct_fab) }}>{r.proj_pct_fab}%</span>
                    {r.has_override && r.pct_fab !== r.proj_pct_fab && (
                      <div style={{ fontSize: '10px', color: '#94a3b8' }}>was {r.pct_fab}%</div>
                    )}
                  </td>
                  <td style={{ ...tdS, backgroundColor: idx % 2 === 0 ? '#f8fbff' : '#eff6ff' }}>
                    {fmtNum(r.earned_credit_install)}
                  </td>
                  <td style={{ ...tdS, backgroundColor: idx % 2 === 0 ? '#f8fbff' : '#eff6ff', fontWeight: '600' }}>
                    <span style={{ color: getProgressColor(r.proj_pct_install) }}>{r.proj_pct_install}%</span>
                    {r.has_override && r.pct_install !== r.proj_pct_install && (
                      <div style={{ fontSize: '10px', color: '#94a3b8' }}>was {r.pct_install}%</div>
                    )}
                  </td>
                  <td style={{ ...tdS, fontWeight: '700' }}>
                    <span style={{ color: getProgressColor(r.proj_pct_total) }}>{r.proj_pct_total}%</span>
                    {r.has_override && r.pct_total !== r.proj_pct_total && (
                      <div style={{ fontSize: '10px', color: '#94a3b8' }}>was {r.pct_total}%</div>
                    )}
                  </td>
                  <td style={{ ...tdS, paddingLeft: 8, paddingRight: 8 }}>
                    <ComparisonBar
                      current={r.pct_total}
                      projected={r.has_override ? r.proj_pct_total : r.pct_total}
                    />
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr style={{ backgroundColor: '#1e293b' }}>
                <td style={{ ...tdS, textAlign: 'left', fontWeight: '700', color: '#fff', borderBottom: 'none' }}>
                  Grand Total
                </td>
                <td style={{ ...tdS, color: '#94a3b8', borderBottom: 'none', fontSize: '11px' }}>
                  {data.rows.length} SOV codes
                </td>
                <td style={{ ...tdS, fontWeight: '700', color: '#fff', borderBottom: 'none' }}>
                  {fmtNum(totals.credit)}
                </td>
                <td style={{ ...tdS, borderBottom: 'none', color: '#94a3b8', fontSize: '11px' }}>
                  {hasAnyOverride ? `+${fmtNum(projectedTotals.credit - totals.credit)}` : '\u2014'}
                </td>
                <td style={{ ...tdS, fontWeight: '700', color: hasAnyOverride ? '#fdba74' : '#fff', borderBottom: 'none' }}>
                  {fmtNum(projectedTotals.credit)}
                </td>
                <td style={{ ...tdS, fontWeight: '700', color: '#c4b5fd', borderBottom: 'none' }}>
                  {fmtNum(projectedTotals.earned_credit_fab)}
                </td>
                <td style={{ ...tdS, fontWeight: '700', color: '#c4b5fd', borderBottom: 'none' }}>
                  {projectedTotals.pct_fab}%
                  {hasAnyOverride && totals.pct_fab !== projectedTotals.pct_fab && (
                    <div style={{ fontSize: '10px', color: '#94a3b8' }}>was {totals.pct_fab}%</div>
                  )}
                </td>
                <td style={{ ...tdS, fontWeight: '700', color: '#7dd3fc', borderBottom: 'none' }}>
                  {fmtNum(projectedTotals.earned_credit_install)}
                </td>
                <td style={{ ...tdS, fontWeight: '700', color: '#7dd3fc', borderBottom: 'none' }}>
                  {projectedTotals.pct_install}%
                  {hasAnyOverride && totals.pct_install !== projectedTotals.pct_install && (
                    <div style={{ fontSize: '10px', color: '#94a3b8' }}>was {totals.pct_install}%</div>
                  )}
                </td>
                <td style={{ ...tdS, fontWeight: '700', color: '#86efac', borderBottom: 'none' }}>
                  {projectedTotals.pct_total}%
                  {hasAnyOverride && totals.pct_total !== projectedTotals.pct_total && (
                    <div style={{ fontSize: '10px', color: '#94a3b8' }}>was {totals.pct_total}%</div>
                  )}
                </td>
                <td style={{ ...tdS, paddingLeft: 8, paddingRight: 8, borderBottom: 'none' }}>
                  <div style={{
                    width: '100%', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 9,
                    height: 18, overflow: 'hidden', position: 'relative',
                  }}>
                    <div style={{
                      width: `${Math.min(projectedTotals.pct_total, 100)}%`, height: '100%',
                      backgroundColor: COLORS.success, borderRadius: 9,
                      transition: 'width 0.6s ease-out',
                    }} />
                    <span style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)', fontSize: '10px',
                      fontWeight: '700', color: '#fff',
                    }}>
                      {projectedTotals.pct_total}%
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer notes */}
      <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8' }}>
        <div>
          {hasAnyOverride && (
            <span style={{ color: COLORS.scenario }}>
              Scenario active — projected values shown in orange
            </span>
          )}
        </div>
        <div>% Total = (Earned Fab + Earned Install) / (Projected Credit x 2)</div>
      </div>
    </div>
  );
};

export default PipingSOVAll;
